import { TypedDocumentNode, gql } from '@apollo/client';
import { ComponentProps, ReactNode, RefObject, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { Lightning } from '@sorare/core/src/atoms/icons/Lightning';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { LabelL, LabelM } from '@sorare/core/src/atoms/typography';
import { Stepper } from '@sorare/core/src/components/onboarding/Dialog/Stepper';
import { ExtraMessageWithBlurredBg } from '@sorare/core/src/components/onboarding/ExtraMessageWithBlurredBg';
import { HighlightOverlay } from '@sorare/core/src/components/onboarding/HighlightOverlay';
import { useIsTabletAndAbove } from '@sorare/core/src/hooks/device/useIsTabletAndAbove';
import { useTransitionApi } from '@sorare/core/src/hooks/ui/useTransitionApi';
import useLifecycle, { LIFECYCLE } from '@sorare/core/src/hooks/useLifecycle';
import { glossary } from '@sorare/core/src/lib/glossary';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

import { MyClubOnboarding_anyCardInterface } from './__generated__/index.graphql';
import bonus from './assets/bonus.png';
import cards from './assets/cards.png';
import firstTimeOwner from './assets/first-time-owner.png';
import rank from './assets/rank.png';

const Cta = styled(Horizontal).attrs({ center: true })`
  position: absolute;
  bottom: var(--quadruple-unit);
  left: var(--double-unit);
  right: var(--double-unit);
  margin: auto;
  & > * {
    width: 100%;
  }
  @media ${tabletAndAbove} {
    & > * {
      width: unset;
      min-width: 300px;
    }
  }
`;

const ExtraMessageContent = styled(Horizontal).attrs({ center: true })`
  text-align: start;
  justify-content: flex-start;
`;

const StepperWrapper = styled.div`
  position: absolute;
  top: var(--double-unit);
  left: var(--unit);
  right: var(--unit);
  @media ${tabletAndAbove} {
    width: 400px;
    left: 50%;
    transform: translateX(-50%);
  }
`;

const OverlayContent = ({
  title,
  description,
  icon,
  step,
  onClick,
  setStep,
  isLastStep,
  positionProps,
}: {
  title: ReactNode;
  description?: ReactNode;
  icon?: ReactNode;
  step: number;
  onClick: () => void;
  positionProps: ComponentProps<
    typeof ExtraMessageWithBlurredBg
  >['positionProps'];
  setStep: (step: number) => void;
  isLastStep?: boolean;
}) => {
  const isTabletAndAbove = useIsTabletAndAbove();
  return (
    <>
      <StepperWrapper>
        <Stepper step={step} length={4} setStep={setStep} />
      </StepperWrapper>
      <ExtraMessageWithBlurredBg
        positionProps={positionProps}
        anchor="bottom"
        align="start"
        fullWidth={!isTabletAndAbove}
      >
        <ExtraMessageContent center>
          {icon}
          <Vertical gap={0}>
            {title}
            {description && (
              <LabelM color="var(--c-neutral-700)">{description}</LabelM>
            )}
          </Vertical>
        </ExtraMessageContent>
      </ExtraMessageWithBlurredBg>
      <Cta>
        <Button
          color={isLastStep ? 'primary' : 'secondary'}
          size="large"
          onClick={onClick}
        >
          <FormattedMessage
            {...(isLastStep ? glossary.letsGo : glossary.continue)}
          />
        </Button>
      </Cta>
    </>
  );
};

const STEPS_DATA = [
  {
    highlightNames: ['ManagerProfileGalleryCard'],
    overlayProps: {
      title: (
        <SBHorizontal>
          <LabelL bold>
            <FormattedMessage
              id="MyClubOnboarding.firstTimeOwnerTitle"
              defaultMessage="First Time Owner"
            />
          </LabelL>
          <Horizontal gap={0.5}>
            <LabelM bold>+30</LabelM>
            <Lightning />
          </Horizontal>
        </SBHorizontal>
      ),
      icon: <img src={firstTimeOwner} alt="" width={36} height={36} />,
    },
    nextTo: 'ManagerProfileGalleryCard',
  },
  {
    highlightNames: [
      'ManagerProfileGalleryCard',
      'ManagerProfileCollectionHeader',
      'ManagerProfileCollectionBonus',
    ],
    overlayProps: {
      title: (
        <LabelL bold>
          <FormattedMessage
            id="MyClubOnboarding.cardBonusTitle"
            defaultMessage="Scoring Bonus"
          />
        </LabelL>
      ),
      description: (
        <FormattedMessage
          id="MyClubOnboarding.cardBonusDescription"
          defaultMessage="Cards can earn a Collection Bonus"
        />
      ),
      icon: <img src={bonus} alt="" width={36} height={36} />,
    },
    nextTo: 'ManagerProfileCollectionBonus',
  },
  {
    highlightNames: [
      'ManagerProfileGalleryCard',
      'ManagerProfileCollectionHeader',
      'ManagerProfileCollectionScore',
      'ManagerProfileCollectionCards',
      'ManagerProfileCollectionBonus',
    ],
    overlayProps: {
      title: (
        <LabelL bold>
          <FormattedMessage
            id="MyClubOnboarding.increaseBonusTitle"
            defaultMessage="Bigger collection, bigger bonus"
          />
        </LabelL>
      ),
      description: (
        <FormattedMessage
          id="MyClubOnboarding.increaseBonusDescription"
          defaultMessage="The more cards you collect from the same Club and season the bigger the bonus"
        />
      ),
      icon: <img src={cards} alt="" width={36} height={36} />,
    },
    nextTo: 'ManagerProfileCollectionBonus',
  },
  {
    highlightNames: [
      'ManagerProfileGalleryCard',
      'ManagerProfileCollectionHeader',
      'ManagerProfileCollectionRank',
    ],
    overlayProps: {
      title: (
        <LabelL bold>
          <FormattedMessage
            id="MyClubOnboarding.climbLeaderboardTitle"
            defaultMessage="Become the top collector"
          />
        </LabelL>
      ),
      description: (
        <FormattedMessage
          id="MyClubOnboarding.climbLeaderboardDescription"
          defaultMessage="Climb the collection leaderboard and get rewarded for your fandom"
        />
      ),
      icon: <img src={rank} alt="" width={36} height={36} />,
    },
    nextTo: 'ManagerProfileCollectionRank',
  },
] satisfies {
  highlightNames: ComponentProps<typeof HighlightOverlay>['highlightNames'];
  overlayProps: Pick<
    ComponentProps<typeof OverlayContent>,
    'title' | 'icon' | 'description'
  >;
  nextTo: ComponentProps<typeof HighlightOverlay>['highlightNames'][number];
}[];

type Props = {
  card: Nullable<MyClubOnboarding_anyCardInterface>;
  collectionsScrollRef: RefObject<HTMLDivElement>;
  galleryScrollRef: RefObject<HTMLDivElement>;
};

export const MyClubOnboarding = ({
  card,
  collectionsScrollRef,
  galleryScrollRef,
}: Props) => {
  const [step, setStep] = useState<number | undefined>(0);
  const { updateDOM } = useTransitionApi();
  const isSmallHeightWindow = window.innerHeight < 700;
  const { update: updateLifecycle } = useLifecycle();
  const onStepChange = async (v: number) => {
    let deffer;
    if (
      (v >= 1 && step === 0) ||
      (v === 0 && step !== undefined && step >= 1)
    ) {
      const { updateCallbackDone } = updateDOM(() => {
        // hide overlay while scrolling to avoid highlight flickering
        setStep(undefined);
      });
      deffer = updateCallbackDone;
    } else {
      deffer = Promise.resolve();
    }

    if (v === 0) {
      galleryScrollRef.current?.scrollIntoView({
        block: 'center',
        behavior: 'smooth',
      });
    } else if (v !== undefined && v >= 1) {
      if (isSmallHeightWindow) {
        collectionsScrollRef.current?.scrollIntoView({
          block: 'center',
          behavior: 'smooth',
        });
      } else {
        galleryScrollRef.current?.scrollIntoView({
          block: 'start',
          behavior: 'smooth',
        });
      }
    }

    // wait for step to be reset eventually before reupdating to prevent concurrency
    await deffer;
    updateDOM(() => {
      setStep(v);
    });
  };

  const onNext = () => {
    if (step === undefined) {
      return;
    }
    if (step === STEPS_DATA.length - 1) {
      updateLifecycle(LIFECYCLE.sawFootballMyClubOnboarding, true);
      setStep(undefined);
    }
    onStepChange(step + 1);
  };

  const stepData = typeof step === 'number' ? STEPS_DATA.at(step) : undefined;
  if (typeof step !== 'number' || !stepData) {
    return null;
  }
  return (
    <HighlightOverlay
      disableBackdropClose
      withDelay
      blur={3}
      onClose={onNext}
      highlightNames={
        isSmallHeightWindow && step > 0
          ? stepData.highlightNames.filter(
              v => v !== 'ManagerProfileGalleryCard'
            )
          : stepData.highlightNames
      }
      extras={[
        {
          render: ({ onClose, ...otherProps }) => (
            <OverlayContent
              positionProps={otherProps}
              {...stepData.overlayProps}
              description={
                step === 0 ? (
                  <FormattedMessage
                    id="MyClubOnboarding.firstTimeOwnerDescription"
                    defaultMessage="Welcome {name} to your collection!"
                    values={{
                      name: card?.anyPlayer?.displayName ?? '',
                    }}
                  />
                ) : (
                  stepData.overlayProps.description
                )
              }
              step={step}
              onClick={onNext}
              setStep={onStepChange}
              isLastStep={step === STEPS_DATA.length - 1}
            />
          ),
          nextTo: stepData.nextTo,
        },
      ]}
    />
  );
};

MyClubOnboarding.fragments = {
  anyCardInterface: gql`
    fragment MyClubOnboarding_anyCardInterface on AnyCardInterface {
      slug
      anyPlayer {
        slug
        displayName
      }
    }
  ` as TypedDocumentNode<MyClubOnboarding_anyCardInterface>,
};
